// Folder/file display names round-trip arbitrary UTF-8 through the path codec, so a decoded
// segment can be anything — including names that break or mangle extraction on Windows. Each
// segment is cleaned before it becomes a ZIP entry.

// Reserved on Windows (and, for `/`, everywhere): path separators, wildcards, quotes, angle
// brackets, pipe, and control characters (\p{Cc}, which covers 0x00-0x1F).
const RESERVED_CHARS = /[\\/:*?"<>|\p{Cc}]/gu;

// Windows device names are reserved regardless of extension (`CON`, `CON.txt`, `con` all clash).
const RESERVED_DEVICE_NAMES = new Set([
  "CON",
  "PRN",
  "AUX",
  "NUL",
  ...Array.from({ length: 9 }, (_, i) => `COM${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `LPT${i + 1}`),
]);

const FALLBACK = "_";

/**
 * Makes a single decoded path label safe as a ZIP entry segment across OSes: replaces reserved and
 * control characters, trims trailing dots/spaces (Windows silently strips them), disarms reserved
 * device names, and never returns an empty, `.`, or `..` segment.
 */
export function sanitizeZipSegment(label: string): string {
  let cleaned = label.replace(RESERVED_CHARS, "_").replace(/[ .]+$/, "");
  if (cleaned === "") return FALLBACK;

  const dotIndex = cleaned.indexOf(".");
  const baseName = dotIndex === -1 ? cleaned : cleaned.slice(0, dotIndex);
  if (RESERVED_DEVICE_NAMES.has(baseName.toUpperCase())) {
    cleaned = dotIndex === -1 ? `${cleaned}_` : `${cleaned.slice(0, dotIndex)}_${cleaned.slice(dotIndex)}`;
  }

  return cleaned;
}
